/**
 * Pure body-region logic for Contour+ model selection: parse DICOM metadata
 * hints, aggregate per-slice image labels into scan coverage, reconcile the
 * two, and match coverage to catalogue body parts. React-free; unit-tested.
 *
 * Confidence is qualitative (HIGH/MEDIUM/LOW/NONE) on purpose — no
 * percentages until validated on our data.
 */
import { BODY_REGIONS } from '@agent/tool-names';
import type { BodyRegion, SliceRegionLabel } from '../../contract';

export type MetadataAgreement = 'AGREE' | 'PARTIAL' | 'CONFLICT' | 'NONE';
export type Confidence = 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';

export interface MetadataHints {
    readonly bodyPartExamined: string;
    readonly seriesDescription: string;
    readonly protocolName: string;
}

export interface MetadataEvidence {
    readonly parsedRegions: BodyRegion[];
    readonly agreement: MetadataAgreement;
}

export interface SliceLabel {
    readonly percentile: number;
    readonly sliceIndex: number;
    readonly region: SliceRegionLabel;
}

const ALL: BodyRegion[] = [...BODY_REGIONS];
const HN: BodyRegion[] = ['HEAD_NECK'];
const C: BodyRegion[] = ['CHEST'];
const A: BodyRegion[] = ['ABDOMEN'];
const P: BodyRegion[] = ['PELVIS'];

// Whole-token vocabulary for BodyPartExamined (DICOM defined terms) and
// common SeriesDescription / ProtocolName words. Unknown tokens are ignored.
const TOKEN_REGIONS: Readonly<Record<string, BodyRegion[]>> = {
    HEAD: HN,
    NECK: HN,
    HEADNECK: HN,
    HN: HN,
    BRAIN: HN,
    SKULL: HN,
    ORBIT: HN,
    SINUS: HN,
    THYROID: HN,
    CSPINE: HN,
    CHEST: C,
    THORAX: C,
    THORACIC: C,
    LUNG: C,
    LUNGS: C,
    BREAST: C,
    TSPINE: C,
    ABDOMEN: A,
    ABDO: A,
    ABD: A,
    ABDOMINAL: A,
    LIVER: A,
    KIDNEY: A,
    RENAL: A,
    PANCREAS: A,
    LSPINE: A,
    PELVIS: P,
    PELVIC: P,
    PROSTATE: P,
    BLADDER: P,
    RECTUM: P,
    RECTAL: P,
    CERVIX: P,
    GYN: P,
    SSPINE: P,
    CHESTABDOMEN: [...C, ...A],
    ABDOMENPELVIS: [...A, ...P],
    ABDOPELVIS: [...A, ...P],
    CHESTABDPELVIS: [...C, ...A, ...P],
    CAP: [...C, ...A, ...P],
    TAP: [...C, ...A, ...P],
    WHOLEBODY: ALL,
    WB: ALL,
};

/** Keep only BODY_REGIONS members, in head→feet order, without duplicates. */
function inAnatomicalOrder(regions: Iterable<BodyRegion>): BodyRegion[] {
    const set = new Set(regions);
    return ALL.filter((r) => set.has(r));
}

/** Regions named by one free-text field. Empty = no useful information. */
export function parseRegionsFromText(text: string): BodyRegion[] {
    const tokens = text
        .toUpperCase()
        .replace(/&/g, '')
        .split(/[^A-Z0-9]+/)
        .filter(Boolean);
    return inAnatomicalOrder(tokens.flatMap((t) => TOKEN_REGIONS[t] ?? []));
}

const sameSet = (a: BodyRegion[], b: BodyRegion[]) =>
    a.length === b.length && a.every((r) => b.includes(r));
const disjoint = (a: BodyRegion[], b: BodyRegion[]) =>
    !a.some((r) => b.includes(r));

/** Parse the three hints and classify how they relate to each other. */
export function metadataEvidence(hints: MetadataHints): MetadataEvidence {
    const informative = [
        hints.bodyPartExamined,
        hints.seriesDescription,
        hints.protocolName,
    ]
        .map(parseRegionsFromText)
        .filter((regions) => regions.length > 0);

    if (informative.length === 0) {
        return { parsedRegions: [], agreement: 'NONE' };
    }
    const parsedRegions = inAnatomicalOrder(informative.flat());
    const pairs = informative.flatMap((a, i) =>
        informative.slice(i + 1).map((b) => [a, b] as const)
    );
    const agreement: MetadataAgreement = pairs.some(([a, b]) => disjoint(a, b))
        ? 'CONFLICT'
        : pairs.every(([a, b]) => sameSet(a, b))
          ? 'AGREE'
          : 'PARTIAL';
    return { parsedRegions, agreement };
}

/**
 * Scan coverage from per-slice labels (ordered along the slice stack). A
 * region counts when:
 *  1. at least 2 samples show it, or
 *  2. it is the first or last informative sample (a scan end naturally gets
 *     only one slice), or
 *  3. it sits between its two neighbouring samples in head→feet anatomy
 *     (PELVIS, ABDOMEN, CHEST: a scan cannot reach from pelvis to chest
 *     without passing the abdomen, so that one slice is consistent).
 * Any other lone label is treated as noise: VLMs tend to report expected but
 * absent anatomy, e.g. one HEAD_NECK slice between two PELVIS slices.
 */
export function aggregateCoverage(
    labels: ReadonlyArray<SliceLabel>
): BodyRegion[] {
    const informative = labels
        .map((l) => l.region)
        .filter((r): r is BodyRegion => (ALL as string[]).includes(r));
    if (informative.length === 0) {
        return [];
    }
    const last = informative.length - 1;
    const rank = (r: BodyRegion): number => ALL.indexOf(r);
    const counted = informative.filter((r, i) => {
        if (i === 0 || i === last) {
            return true;
        }
        if (informative.filter((x) => x === r).length >= 2) {
            return true;
        }
        const before = rank(informative[i - 1]);
        const after = rank(informative[i + 1]);
        return (
            Math.min(before, after) < rank(r) &&
            rank(r) < Math.max(before, after)
        );
    });
    return inAnatomicalOrder(counted);
}

export interface Reconciled {
    readonly detectedBodyRegions: BodyRegion[];
    readonly confidence: Confidence;
    readonly metadataConflict: boolean;
    readonly method: string;
}

/** Combine metadata and image evidence; a conflict is kept, never hidden. */
export function reconcile(
    metadata: MetadataEvidence,
    visionCoverage: BodyRegion[]
): Reconciled {
    const meta = metadata.parsedRegions;
    const hasMeta = meta.length > 0;

    if (visionCoverage.length === 0) {
        return {
            detectedBodyRegions: meta,
            confidence: !hasMeta
                ? 'NONE'
                : metadata.agreement === 'AGREE'
                  ? 'MEDIUM'
                  : 'LOW',
            metadataConflict: metadata.agreement === 'CONFLICT',
            method: hasMeta ? 'metadata only' : 'no evidence',
        };
    }

    const method = hasMeta
        ? 'metadata + representative-slice vision'
        : 'representative-slice vision';
    if (!hasMeta) {
        return {
            detectedBodyRegions: visionCoverage,
            confidence: 'MEDIUM',
            metadataConflict: false,
            method,
        };
    }
    if (disjoint(meta, visionCoverage)) {
        return {
            detectedBodyRegions: visionCoverage,
            confidence: 'LOW',
            metadataConflict: true,
            method,
        };
    }
    const fieldsDisagree = metadata.agreement === 'CONFLICT';
    return {
        detectedBodyRegions: visionCoverage,
        confidence:
            sameSet(meta, visionCoverage) && !fieldsDisagree
                ? 'HIGH'
                : 'MEDIUM',
        metadataConflict: fieldsDisagree,
        method,
    };
}

// Catalogue body_part strings, normalised (lowercase letters only, "&" →
// "and"). The backend's exact spelling varies between screens, so matching is
// spelling-tolerant. An unknown body part matches nothing.
const BODY_PART_REGIONS: Readonly<Record<string, BodyRegion[]>> = {
    head: HN,
    brain: HN,
    headandneck: HN,
    headneck: HN,
    hn: HN,
    chest: C,
    thorax: C,
    lung: C,
    breast: C,
    abdomen: A,
    abdomenandchest: [...C, ...A],
    abdomenchest: [...C, ...A],
    chestandabdomen: [...C, ...A],
    chestabdomen: [...C, ...A],
    torso: [...C, ...A, ...P],
    pelvis: P,
    malepelvis: P,
    femalepelvis: P,
    prostate: P,
    combination: ALL,
    wholebody: ALL,
};

export function regionsForBodyPart(bodyPart: string): BodyRegion[] {
    const key = bodyPart
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z]/g, '');
    return BODY_PART_REGIONS[key] ?? [];
}

/** A model fits when its body part covers at least one detected region. */
export function bodyPartMatchesCoverage(
    bodyPart: string,
    coverage: ReadonlyArray<BodyRegion>
): boolean {
    return regionsForBodyPart(bodyPart).some((r) => coverage.includes(r));
}
